export interface User {
  id: number
  name: string
  email: string
  role: 'admin' | 'user'
  household_id: number | null
  needs_household_setup: boolean
}

export interface Bill {
  id: number
  name: string
  amount: number
  due_day: number
  due_date: string
  frequency: string
  category_id: number | null
  category_name: string | null
  category_icon: string | null
  category_color: string | null
  autopay: boolean
  status: 'pending' | 'paid' | 'overdue'
  days_until_due: number
  notes: string | null
  url: string | null
  merchant_pattern: string | null
  active: number
  created_at: string
  updated_at: string
}

export interface Category {
  id: number
  name: string
  icon: string | null
  color: string | null
  sort_order: number
  type: 'bill' | 'expense' | 'income'
  budget_amount: number | null
}

export interface Transaction {
  id: number
  merchant: string
  amount: number
  date: string
  type: 'expense' | 'income'
  category_id: number | null
  category_name: string | null
  category_icon: string | null
  category_color: string | null
  bank_account_id: number | null
  bank_name: string | null
  notes: string | null
  linked_bill_id: number | null
  linked_bill_name: string | null
  tax_deductible: boolean
  tax_category: TaxCategory | null
  created_at: string
}

export interface Payment {
  id: number
  bill_id: number
  amount: number
  payment_date: string
  confirmation_number: string | null
  created_at: string
}

export interface BankAccount {
  id: number
  name: string
  institution: string | null
  account_type: string
  last_four: string | null
  balance: number | null
  is_active: boolean
}

export interface SpendingSummary {
  category_id: number
  category_name: string
  category_icon: string | null
  category_color: string | null
  total_amount: number
  transaction_count: number
  budget_amount?: number | null
  budget_percentage?: number | null
  over_budget?: boolean
}

export interface IncomeSummary {
  source_name: string
  category_id: number | null
  category_name: string | null
  category_icon: string | null
  category_color: string | null
  total_amount: number
  transaction_count: number
}

export interface Notification {
  id: number
  user_id: number
  type: string
  title: string
  message: string | null
  severity: 'info' | 'warning' | 'error'
  read: number
  data: string | null
  created_at: string
}

export interface Household {
  id: number
  name: string
  created_by: number
  invite_code: string
  created_at: string
  member_count?: number
  role?: 'admin' | 'member'
}

export interface HouseholdMember {
  id: number
  user_id: number
  household_id: number
  role: 'admin' | 'member'
  joined_at: string
  user_name: string
  user_email: string
}

export interface HouseholdInvitation {
  id: number
  household_id: number
  household_name: string
  email: string
  invited_by: number
  inviter_name: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  created_at: string
  expires_at: string | null
}

export interface AnomalyAlert {
  id: number
  household_id: number
  transaction_id: number | null
  category_id: number | null
  alert_type: 'unusual_amount' | 'budget_exceeded' | 'unusual_frequency'
  severity: 'info' | 'warning' | 'error'
  title: string
  message: string | null
  expected_value: number | null
  actual_value: number | null
  deviation_percent: number | null
  dismissed: number
  dismissed_by: number | null
  dismissed_at: string | null
  created_at: string
  transaction_merchant?: string
  transaction_amount?: number
  transaction_date?: string
  category_name?: string
  category_color?: string
  dismissed_by_name?: string
}

export interface MonthlyCashFlow {
  month: string   // 'YYYY-MM'
  income: number
  expense: number
}

export interface SavingsGoal {
  id: number
  household_id: number | null
  name: string
  target_amount: number
  current_amount: number
  target_date: string | null
  bank_account_id: number | null
  bank_account_name: string | null
  color: string
  icon: string
  created_at: string
  updated_at: string
}

export type TaxCategory =
  | 'Business Expense'
  | 'Charitable Donation'
  | 'Medical'
  | 'Home Office'
  | 'Other'

export const TAX_CATEGORIES: TaxCategory[] = [
  'Business Expense',
  'Charitable Donation',
  'Medical',
  'Home Office',
  'Other',
]

export interface CreateSavingsGoalData {
  name: string
  target_amount: number
  current_amount?: number
  target_date?: string | null
  bank_account_id?: number | null
  color?: string
  icon?: string
}

export interface ReminderPreferences {
  id: number
  household_id: number
  enabled: boolean
  days_before: number[]
  send_overdue: boolean
  created_at: string
  updated_at: string
}

export interface FinanceCsvPreviewRow {
  rowIndex: number
  date: string
  merchant: string
  amount: number
  notes: string | null
  category_name: string | null
  matched_category_id: number | null
  isDuplicate: boolean
}

export interface FinanceCsvPreviewResult {
  format: 'ynab' | 'mint' | 'monarch' | 'unknown'
  totalRows: number
  parseErrors: number
  duplicates: number
  rows: FinanceCsvPreviewRow[]
}

export interface FinanceCsvApplyResult {
  imported: number
  skipped: number
  errors: number
  format: string
}

export type DebtType =
  | 'credit_card' | 'student_loan' | 'auto_loan'
  | 'personal_loan' | 'mortgage' | 'other'

export interface Debt {
  id: number
  household_id: number
  name: string
  debt_type: DebtType
  current_balance: number
  original_balance: number
  interest_rate: number
  minimum_payment: number
  bank_account_id: number | null
  account_name: string | null
  notes: string | null
  created_at: string
  paid_off_at: string | null
  source_bill_id: number | null
}

export interface DebtScheduleEntry {
  month: number
  total_balance: number
}

export interface DebtPayoffItem {
  id: number
  name: string
  current_balance: number
  interest_rate: number
  minimum_payment: number
  months_to_payoff: number | null
  total_interest: number
  payoff_date: string | null
}

export interface PayoffPlan {
  method: 'snowball' | 'avalanche'
  extra_payment: number
  total_months: number
  total_interest: number
  payoff_date: string | null
  debts: DebtPayoffItem[]
  monthly_schedule: DebtScheduleEntry[]
}

export interface CreateDebtData {
  name: string
  debt_type: DebtType
  current_balance: number
  interest_rate: number
  minimum_payment: number
  bank_account_id?: number | null
  notes?: string | null
}

export interface NetWorthSnapshot {
  snapshot_date: string
  assets: number
  liabilities: number
  net_worth: number
}

export interface NetWorthCurrent {
  assets: number
  liabilities: number
  netWorth: number
  changeFromLastMonth: number | null
}

export interface NetWorthHistory {
  snapshots: NetWorthSnapshot[]
  months: number
}
